import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    View,
    TouchableOpacity,
    StyleSheet,
    KeyboardAvoidingView,
    Platform,
    StatusBar,
    TextInput,
    ActivityIndicator,
    ScrollView,
    Animated,
    Modal,
    Keyboard,
    useWindowDimensions,
} from 'react-native';
// `Alert` daqui, nao do react-native: no navegador o Alert do RN e um stub
// vazio e engole tudo em silencio (ver src/utils/appAlert.js). Em nativo e
// reexportacao direta - comportamento do app inalterado.
import { Alert } from '../utils/appAlert';
import Reanimated, {
    useSharedValue,
    useAnimatedStyle,
    withTiming,
    withSpring,
    interpolate,
    Extrapolation,
    Easing,
} from 'react-native-reanimated';
// expo-image (e não o Image do react-native): o logo é WebP, formato que o
// Image do RN não decodifica no iOS.
import { Image } from 'expo-image';
import { Text } from '../components/ui/AppText';
import GoogleIcon from '../components/ui/GoogleIcon';
import { LinearGradient } from 'expo-linear-gradient';
import { useAuth } from '../contexts/AuthContext';
import { signIn, signUp, resetPassword, signInWithGoogle } from '../services/authService';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { styles } from './UserAuthScreen.styles';

// Respiro entre a safe area e a marca. Espelha o `paddingTop` do `header` do
// OnboardingScreen — as duas telas mostram o mesmo logo, e ele não pode pular
// de altura na transição. Se mudar lá, mudar aqui.
const BRAND_TOP_GAP = 18;
const HERO_EXPANDED_MAX_HEIGHT = 260;

const normalizeInitialTab = (route) => {
    const requested = route?.params?.initialTab || route?.params?.mode;
    return requested === 'register' ? 'register' : 'login';
};

const getPasswordRules = (password) => [
    { label: '8 caracteres', valid: password.length >= 8 },
    { label: '1 letra', valid: /[a-zA-Z]/.test(password) },
    { label: '1 número', valid: /[0-9]/.test(password) },
];

const Field = ({
    label,
    placeholder,
    value,
    onChangeText,
    keyboardType = 'default',
    secureTextEntry = false,
    autoCapitalize = 'none',
    rightElement = null,
    editable = true,
    compact = false,
    ultraCompact = false,
}) => (
    <View style={[
        styles.field,
        compact && styles.fieldCompact,
        ultraCompact && styles.fieldUltraCompact,
    ]}>
        <Text style={[
            styles.fieldLabel,
            compact && styles.fieldLabelCompact,
            ultraCompact && styles.fieldLabelUltraCompact,
        ]}>{label}</Text>
        <View style={[
            styles.fieldControl,
            compact && styles.fieldControlCompact,
            ultraCompact && styles.fieldControlUltraCompact,
        ]}>
            <TextInput
                style={[
                    styles.fieldInput,
                    compact && styles.fieldInputCompact,
                    ultraCompact && styles.fieldInputUltraCompact,
                    rightElement && styles.fieldInputWithAction,
                ]}
                placeholder={placeholder}
                placeholderTextColor="#A0A3B1"
                value={value}
                onChangeText={onChangeText}
                keyboardType={keyboardType}
                autoCapitalize={autoCapitalize}
                autoCorrect={false}
                secureTextEntry={secureTextEntry}
                editable={editable}
            />
            {rightElement}
        </View>
    </View>
);

const UserAuthScreen = ({ navigation, route }) => {
    const { isAuthenticated } = useAuth();
    const insets = useSafeAreaInsets();
    const { width: windowWidth, height: windowHeight } = useWindowDimensions();
    const effectiveWindowWidth = Platform.OS === 'web' && Number.isFinite(globalThis?.innerWidth)
        ? globalThis.innerWidth
        : windowWidth;
    const effectiveWindowHeight = Platform.OS === 'web' && Number.isFinite(globalThis?.innerHeight)
        ? globalThis.innerHeight
        : windowHeight;

    const returnTo = route?.params?.returnTo || 'Home';
    const [activeTab, setActiveTab] = useState(normalizeInitialTab(route));
    const isLoginTab = activeTab === 'login';
    const compactLogin = isLoginTab && (effectiveWindowHeight <= 820 || effectiveWindowWidth <= 380);
    const veryCompactLogin = isLoginTab && (effectiveWindowHeight <= 740 || effectiveWindowWidth <= 360);
    const ultraCompactLogin = isLoginTab && (effectiveWindowHeight <= 640 || effectiveWindowWidth <= 340);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [nome, setNome] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showAccountCreatedModal, setShowAccountCreatedModal] = useState(false);
    const [createdAccountEmail, setCreatedAccountEmail] = useState('');
    // Mini-modal de recuperação de senha (campo de e-mail dedicado).
    const [forgotVisible, setForgotVisible] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);

    const sheetTranslateY = useRef(new Animated.Value(24)).current;
    const sheetOpacity = useRef(new Animated.Value(0)).current;
    const blockAuthRedirectRef = useRef(false);
    // Marca que esta sessão veio de um CADASTRO (e não de um login). Quem decide
    // o destino é o `redirectAfterAuth` abaixo — em um lugar só.
    const justRegisteredRef = useRef(false);

    // Colapsa o hero (título/métricas) enquanto o teclado está aberto, dando a
    // área toda ao formulário — o campo focado fica rente ao teclado.
    // Reanimated → a animação de height roda na thread de UI (worklet), suave
    // mesmo com o teclado subindo e o foco do campo ocupando a thread JS.
    const heroProgress = useSharedValue(0); // 0 = expandido, 1 = colapsado

    const heroAnimatedStyle = useAnimatedStyle(() => ({
        opacity: interpolate(heroProgress.value, [0, 1], [1, 0], Extrapolation.CLAMP),
        maxHeight: interpolate(
            heroProgress.value,
            [0, 1],
            [HERO_EXPANDED_MAX_HEIGHT, 0],
            Extrapolation.CLAMP,
        ),
    }), []);

    // Pílula branca que desliza sob a aba ativa (Entrar/Cadastrar).
    // 0 = login (esquerda), 1 = register (direita). Anima com mola (withSpring)
    // na thread de UI quando `activeTab` muda.
    const segmentProgress = useSharedValue(activeTab === 'register' ? 1 : 0);
    const [segWidth, setSegWidth] = useState(0);
    const segPillWidth = segWidth > 0 ? (segWidth - 12) / 2 : 0; // padding 4*2 + gap 4

    useEffect(() => {
        segmentProgress.value = withSpring(activeTab === 'register' ? 1 : 0, {
            damping: 18,
            stiffness: 200,
            mass: 0.6,
        });
    }, [activeTab, segmentProgress]);

    const segPillStyle = useAnimatedStyle(() => ({
        transform: [{ translateX: segmentProgress.value * (segPillWidth + 4) }],
    }), [segPillWidth]);

    useEffect(() => {
        Animated.parallel([
            Animated.timing(sheetTranslateY, {
                toValue: 0,
                duration: 360,
                useNativeDriver: true,
            }),
            Animated.timing(sheetOpacity, {
                toValue: 1,
                duration: 300,
                useNativeDriver: true,
            }),
        ]).start();
    }, [sheetOpacity, sheetTranslateY]);

    useEffect(() => {
        const animateHero = (toValue) => {
            heroProgress.value = withTiming(toValue, {
                duration: 240,
                easing: Easing.out(Easing.cubic),
            });
        };
        const showSub = Keyboard.addListener('keyboardDidShow', () => animateHero(1));
        const hideSub = Keyboard.addListener('keyboardDidHide', () => animateHero(0));
        return () => {
            showSub.remove();
            hideSub.remove();
        };
    }, [heroProgress]);

    // ⚠️ Destino pós-autenticação em UM lugar só.
    //
    // Havia uma CORRIDA: o efeito abaixo redireciona assim que a autenticação
    // chega, enquanto o botão do modal de "conta criada" também redirecionava.
    // Quem chegasse primeiro ganhava — e se a autenticação chegasse DEPOIS do
    // toque, o efeito mandava para a Home e o onboarding era pulado. Como o
    // estado de auth chega de forma assíncrona, o comportamento era aleatório.
    //
    // Agora ambos chamam esta função. A flag garante idempotência: o primeiro a
    // rodar consome o "acabou de se cadastrar" e o segundo vira no-op.
    const redirectAfterAuth = useCallback(() => {
        if (justRegisteredRef.current) {
            justRegisteredRef.current = false;
            navigation.replace('ProfileSetupCourse', { returnTo });
            return;
        }
        navigation.replace(returnTo);
    }, [navigation, returnTo]);

    useEffect(() => {
        if (isAuthenticated && !blockAuthRedirectRef.current) {
            redirectAfterAuth();
        }
    }, [isAuthenticated, redirectAfterAuth]);

    const goBack = () => {
        if (navigation.canGoBack?.()) {
            navigation.goBack();
            return;
        }
        // Sem pilha (entrada por deep link `auth-callback`). Home é gated desde
        // 2026-08-05: mandar um deslogado para lá faria o guard rebater para cá.
        navigation.replace(isAuthenticated ? 'Home' : 'Onboarding');
    };

    const switchTab = (tab) => {
        if (loading) return;
        setActiveTab(tab);
    };

    const handleLogin = async () => {
        if (!email || !password) {
            Alert.alert('Erro', 'Por favor, preencha todos os campos');
            return;
        }

        setLoading(true);
        const result = await signIn(email.trim().toLowerCase(), password);
        setLoading(false);

        if (!result.success) {
            Alert.alert('Erro no Login', result.error);
            return;
        }

        navigation.replace(returnTo);
    };

    const handleRegister = async () => {
        if (!nome || !email || !password || !confirmPassword) {
            Alert.alert('Erro', 'Por favor, preencha todos os campos');
            return;
        }

        if (password !== confirmPassword) {
            Alert.alert('Erro', 'As senhas não coincidem');
            return;
        }

        const invalidRule = getPasswordRules(password).find((rule) => !rule.valid);
        if (invalidRule) {
            Alert.alert('Senha inválida', 'A senha deve ter pelo menos 8 caracteres, 1 letra e 1 número.');
            return;
        }

        blockAuthRedirectRef.current = true;
        const normalizedEmail = email.trim().toLowerCase();
        setLoading(true);
        const result = await signUp(normalizedEmail, password, nome.trim());
        setLoading(false);

        if (!result.success) {
            blockAuthRedirectRef.current = false;
            Alert.alert('Erro no Cadastro', result.error);
            return;
        }

        justRegisteredRef.current = true;
        setCreatedAccountEmail(normalizedEmail);
        setPassword('');
        setConfirmPassword('');
        setActiveTab('login');
        setShowAccountCreatedModal(true);
    };

    const handleAccountCreatedContinue = () => {
        setShowAccountCreatedModal(false);
        blockAuthRedirectRef.current = false;

        // Conta recém-criada passa pelo onboarding antes da Home. Se a autenticação
        // ainda não tiver chegado, não faz nada aqui: o efeito acima cuida quando
        // chegar, pelo mesmo `redirectAfterAuth`.
        if (isAuthenticated) {
            redirectAfterAuth();
        }
    };

    // Abre o mini-modal, pré-preenchendo com o e-mail já digitado no login (se houver).
    const openForgotPassword = () => {
        if (loading) return;
        setForgotEmail(email || '');
        setForgotVisible(true);
    };

    const handleSendReset = async () => {
        const target = forgotEmail.trim().toLowerCase();
        if (!target) {
            Alert.alert('Email necessário', 'Digite seu email para recuperar a senha');
            return;
        }

        setForgotLoading(true);
        const result = await resetPassword(target);
        setForgotLoading(false);

        if (result.success) {
            setForgotVisible(false);
            Alert.alert('Email enviado', 'Verifique sua caixa de entrada para redefinir a senha');
            return;
        }

        Alert.alert('Erro', result.error);
    };

    const handleGoogleLogin = async () => {
        try {
            setLoading(true);
            const result = await signInWithGoogle();

            if (!result.success) {
                if (!result.cancelled) {
                    Alert.alert('Erro no Google', result.error);
                }
                return;
            }

            navigation.replace(returnTo);
        } catch (error) {
            Alert.alert('Erro no Google', error.message || 'Não foi possível concluir o login.');
        } finally {
            setLoading(false);
        }
    };

    const passwordRules = getPasswordRules(password);

    return (
        <View style={styles.container}>
            <StatusBar barStyle="light-content" translucent backgroundColor="transparent" />

            <LinearGradient
                // Paleta derivada da cor-base #03386C (hue 210, sat 95%) em 4
                // luminosidades crescentes — mesma progressao escuro -> vivo que
                // o roxo anterior fazia. Textos sobre ela seguem claros.
                colors={['#01172D', '#03386C', '#055AAE', '#0674DF']}
                locations={[0, 0.45, 0.76, 1]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={StyleSheet.absoluteFill}
                pointerEvents="none"
            />
            <View style={styles.backgroundPlaneTop} pointerEvents="none" />
            <View style={styles.backgroundPlaneBottom} pointerEvents="none" />
            <View style={styles.dotLayer} pointerEvents="none">
                {Array.from({ length: 36 }).map((_, index) => (
                    <View key={index} style={styles.dot} />
                ))}
            </View>

            <KeyboardAvoidingView
                // paddingTop inline (e não no styles): passou a depender da safe
                // area, para casar com o OnboardingScreen. Antes era um 38/52
                // fixo — que, de quebra, escondia a marca sob a Dynamic Island,
                // onde insets.top chega a 59.
                style={[
                    styles.content,
                    { paddingTop: insets.top + (ultraCompactLogin ? 6 : compactLogin ? 10 : BRAND_TOP_GAP) },
                ]}
                behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            >
                <View style={styles.safeContent}>
                <View style={[
                    styles.topBar,
                    ultraCompactLogin && styles.topBarUltraCompact,
                ]}>
                    <View style={styles.brand}>
                        {/* Mesma marca da tela de boas-vindas (OnboardingScreen).
                            Antes: assets/icon.png — o ícone herdado do QuizFlow —
                            com a legenda "Concursos", resquício do clone. */}
                        <Image
                            source={require('../../assets/logo-uniquest.webp')}
                            style={[
                                styles.logo,
                                ultraCompactLogin && styles.logoUltraCompact,
                            ]}
                            contentFit="contain"
                            transition={0}
                            accessibilityIgnoresInvertColors
                        />
                        <View style={styles.brandTextBlock}>
                            <Text style={[
                                styles.brandName,
                                ultraCompactLogin && styles.brandNameUltraCompact,
                            ]} numberOfLines={1}>UNIQUEST</Text>
                            <Text style={[
                                styles.brandCaption,
                                ultraCompactLogin && styles.brandCaptionUltraCompact,
                            ]} numberOfLines={1}>
                                Centro Universitário de João Pessoa
                            </Text>
                        </View>
                    </View>

                    <TouchableOpacity style={styles.exitButton} onPress={goBack} activeOpacity={0.75}>
                        <Text style={[
                            styles.exitText,
                            ultraCompactLogin && styles.exitTextUltraCompact,
                        ]}>Sair</Text>
                        <Text style={[
                            styles.exitChevron,
                            ultraCompactLogin && styles.exitChevronUltraCompact,
                        ]}>›</Text>
                    </TouchableOpacity>
                </View>

                <Reanimated.View
                    style={[{ width: '100%', overflow: 'hidden' }, heroAnimatedStyle]}
                >
                    <View
                        style={[
                            styles.hero,
                            compactLogin && styles.heroCompact,
                            veryCompactLogin && styles.heroVeryCompact,
                            ultraCompactLogin && styles.heroUltraCompact,
                        ]}
                    >
                        <Text style={[
                            styles.heroTitle,
                            compactLogin && styles.heroTitleCompact,
                            veryCompactLogin && styles.heroTitleVeryCompact,
                            ultraCompactLogin && styles.heroTitleUltraCompact,
                        ]}>Sua aprovação começa aqui</Text>
                        <Text style={[
                            styles.heroSubtitle,
                            compactLogin && styles.heroSubtitleCompact,
                            veryCompactLogin && styles.heroSubtitleVeryCompact,
                            ultraCompactLogin && styles.heroSubtitleUltraCompact,
                        ]}>
                            Treine questões, revise melhor e acompanhe sua evolução em um só lugar.
                        </Text>

                        {!veryCompactLogin && (
                            <View style={[
                                styles.metricsRow,
                                compactLogin && styles.metricsRowCompact,
                            ]}>
                                {[
                                    { value: '10K+', label: 'Questões' },
                                    { value: '30d', label: 'Revisão' },
                                    { value: '95%', label: 'Foco' },
                                ].map((metric) => (
                                    <View key={metric.label} style={styles.metricItem}>
                                        <Text style={[
                                            styles.metricValue,
                                            compactLogin && styles.metricValueCompact,
                                        ]}>{metric.value}</Text>
                                        <Text style={[
                                            styles.metricLabel,
                                            compactLogin && styles.metricLabelCompact,
                                        ]}>{metric.label}</Text>
                                    </View>
                                ))}
                            </View>
                        )}
                    </View>
                </Reanimated.View>

                <Animated.View
                    style={[
                        styles.sheet,
                        {
                            opacity: sheetOpacity,
                            transform: [{ translateY: sheetTranslateY }],
                        },
                    ]}
                >
                    <View style={[
                        styles.sheetHandleWrap,
                        ultraCompactLogin && styles.sheetHandleWrapUltraCompact,
                    ]}>
                        <View style={styles.sheetHandle} />
                    </View>

                    <ScrollView
                        style={styles.sheetScroll}
                        contentContainerStyle={[
                            styles.sheetContent,
                            compactLogin && styles.sheetContentCompact,
                            veryCompactLogin && styles.sheetContentVeryCompact,
                            ultraCompactLogin && styles.sheetContentUltraCompact,
                        ]}
                        keyboardShouldPersistTaps="handled"
                        horizontal={false}
                        bounces={false}
                        alwaysBounceHorizontal={false}
                        showsVerticalScrollIndicator={false}
                    >
                        <View style={[
                            styles.sheetHeader,
                            compactLogin && styles.sheetHeaderCompact,
                            ultraCompactLogin && styles.sheetHeaderUltraCompact,
                        ]}>
                            <Text style={[
                                styles.eyebrow,
                                ultraCompactLogin && styles.eyebrowUltraCompact,
                            ]}>
                                {activeTab === 'login' ? 'Bem-vindo de volta' : 'Comece hoje'}
                            </Text>
                            <Text style={[
                                styles.sheetTitle,
                                ultraCompactLogin && styles.sheetTitleUltraCompact,
                            ]}>
                                {activeTab === 'login' ? 'Acesse sua conta' : 'Crie sua conta'}
                            </Text>
                        </View>

                        <View
                            style={[
                                styles.segmentedControl,
                                compactLogin && styles.segmentedControlCompact,
                                ultraCompactLogin && styles.segmentedControlUltraCompact,
                            ]}
                            onLayout={(e) => setSegWidth(e.nativeEvent.layout.width)}
                        >
                            {segPillWidth > 0 && (
                                <Reanimated.View
                                    pointerEvents="none"
                                    style={[
                                        styles.segmentPill,
                                        compactLogin && styles.segmentPillCompact,
                                        ultraCompactLogin && styles.segmentPillUltraCompact,
                                        { width: segPillWidth },
                                        segPillStyle,
                                    ]}
                                />
                            )}
                            <TouchableOpacity
                                style={[
                                    styles.segmentButton,
                                    compactLogin && styles.segmentButtonCompact,
                                    ultraCompactLogin && styles.segmentButtonUltraCompact,
                                ]}
                                onPress={() => switchTab('login')}
                                disabled={loading}
                                activeOpacity={0.8}
                            >
                                <Text style={[
                                    styles.segmentText,
                                    ultraCompactLogin && styles.segmentTextUltraCompact,
                                    activeTab === 'login' && styles.segmentTextActive,
                                ]}>
                                    Entrar
                                </Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={[
                                    styles.segmentButton,
                                    compactLogin && styles.segmentButtonCompact,
                                    ultraCompactLogin && styles.segmentButtonUltraCompact,
                                ]}
                                onPress={() => switchTab('register')}
                                disabled={loading}
                                activeOpacity={0.8}
                            >
                                <Text style={[
                                    styles.segmentText,
                                    ultraCompactLogin && styles.segmentTextUltraCompact,
                                    activeTab === 'register' && styles.segmentTextActive,
                                ]}>
                                    Cadastrar
                                </Text>
                            </TouchableOpacity>
                        </View>

                        {activeTab === 'register' && (
                            <Field
                                label="Nome completo"
                                placeholder="Seu nome"
                                value={nome}
                                onChangeText={setNome}
                                autoCapitalize="words"
                                editable={!loading}
                                compact={compactLogin}
                                ultraCompact={ultraCompactLogin}
                            />
                        )}

                        <Field
                            label="Email"
                            placeholder="voce@email.com"
                            value={email}
                            onChangeText={setEmail}
                            keyboardType="email-address"
                            editable={!loading}
                            compact={compactLogin}
                            ultraCompact={ultraCompactLogin}
                        />

                        <Field
                            label="Senha"
                            placeholder="Digite sua senha"
                            value={password}
                            onChangeText={setPassword}
                            secureTextEntry={!showPassword}
                            editable={!loading}
                            compact={compactLogin}
                            ultraCompact={ultraCompactLogin}
                            rightElement={(
                                <TouchableOpacity
                                    style={[
                                        styles.passwordAction,
                                        ultraCompactLogin && styles.passwordActionUltraCompact,
                                    ]}
                                    onPress={() => setShowPassword((current) => !current)}
                                    activeOpacity={0.75}
                                >
                                    <Text style={[
                                        styles.passwordActionText,
                                        ultraCompactLogin && styles.passwordActionTextUltraCompact,
                                    ]}>
                                        {showPassword ? 'Ocultar' : 'Ver'}
                                    </Text>
                                </TouchableOpacity>
                            )}
                        />

                        {activeTab === 'register' && (
                            <>
                                <Field
                                    label="Confirmar senha"
                                    placeholder="Repita sua senha"
                                    value={confirmPassword}
                                    onChangeText={setConfirmPassword}
                                    secureTextEntry={!showPassword}
                                    editable={!loading}
                                    compact={compactLogin}
                                    ultraCompact={ultraCompactLogin}
                                />
                                <View style={styles.passwordRules}>
                                    {passwordRules.map((rule) => (
                                        <View
                                            key={rule.label}
                                            style={[
                                                styles.passwordRulePill,
                                                rule.valid && styles.passwordRulePillValid,
                                            ]}
                                        >
                                            <Text
                                                style={[
                                                    styles.passwordRuleText,
                                                    rule.valid && styles.passwordRuleTextValid,
                                                ]}
                                            >
                                                {rule.label}
                                            </Text>
                                        </View>
                                    ))}
                                </View>
                            </>
                        )}

                        {activeTab === 'login' && (
                            <TouchableOpacity
                                style={[
                                    styles.forgotPassword,
                                    ultraCompactLogin && styles.forgotPasswordUltraCompact,
                                ]}
                                onPress={openForgotPassword}
                                disabled={loading}
                                activeOpacity={0.75}
                            >
                                <Text style={[
                                    styles.forgotPasswordText,
                                    ultraCompactLogin && styles.forgotPasswordTextUltraCompact,
                                ]}>Esqueci minha senha</Text>
                            </TouchableOpacity>
                        )}

                        <TouchableOpacity
                            style={[
                                styles.mainButton,
                                compactLogin && styles.mainButtonCompact,
                                ultraCompactLogin && styles.mainButtonUltraCompact,
                                loading && styles.mainButtonDisabled,
                            ]}
                            onPress={activeTab === 'login' ? handleLogin : handleRegister}
                            disabled={loading}
                            activeOpacity={0.85}
                        >
                            {loading ? (
                                // Escuro, não branco: o botão agora é amarelo e
                                // um spinner branco praticamente desaparece nele.
                                <ActivityIndicator color="#0A3768" />
                            ) : (
                                <Text style={[
                                    styles.mainButtonText,
                                    ultraCompactLogin && styles.mainButtonTextUltraCompact,
                                ]}>
                                    {activeTab === 'login' ? 'Entrar' : 'Criar conta'}
                                </Text>
                            )}
                        </TouchableOpacity>

                        <View style={[
                            styles.dividerRow,
                            compactLogin && styles.dividerRowCompact,
                            veryCompactLogin && styles.dividerRowVeryCompact,
                            ultraCompactLogin && styles.dividerRowUltraCompact,
                        ]}>
                            <View style={styles.dividerLine} />
                            <Text style={styles.dividerText}>ou</Text>
                            <View style={styles.dividerLine} />
                        </View>

                        <TouchableOpacity
                            style={[
                                styles.socialButton,
                                compactLogin && styles.socialButtonCompact,
                                ultraCompactLogin && styles.socialButtonUltraCompact,
                            ]}
                            onPress={handleGoogleLogin}
                            disabled={loading}
                            activeOpacity={0.85}
                        >
                            <View style={[
                                styles.socialIconBadge,
                                ultraCompactLogin && styles.socialIconBadgeUltraCompact,
                            ]}>
                                <GoogleIcon size={ultraCompactLogin ? 17 : 20} />
                            </View>
                            <Text style={[
                                styles.socialButtonText,
                                ultraCompactLogin && styles.socialButtonTextUltraCompact,
                            ]}>Continuar com Google</Text>
                        </TouchableOpacity>

                        <View style={[
                            styles.footer,
                            compactLogin && styles.footerCompact,
                            veryCompactLogin && styles.footerVeryCompact,
                            ultraCompactLogin && styles.footerUltraCompact,
                        ]}>
                            <Text style={[
                                styles.footerText,
                                ultraCompactLogin && styles.footerTextUltraCompact,
                            ]}>
                                {activeTab === 'login' ? 'Não tem conta?' : 'Já tem conta?'}
                            </Text>
                            <TouchableOpacity
                                onPress={() => switchTab(activeTab === 'login' ? 'register' : 'login')}
                                disabled={loading}
                                activeOpacity={0.75}
                            >
                                <Text style={[
                                    styles.footerAction,
                                    ultraCompactLogin && styles.footerActionUltraCompact,
                                ]}>
                                    {activeTab === 'login' ? 'Cadastre-se' : 'Entrar'}
                                </Text>
                            </TouchableOpacity>
                        </View>

                        <View style={[
                            styles.legalLinks,
                            compactLogin && styles.legalLinksCompact,
                            veryCompactLogin && styles.legalLinksVeryCompact,
                            ultraCompactLogin && styles.legalLinksUltraCompact,
                        ]}>
                            <TouchableOpacity onPress={() => Alert.alert('Termos de Uso', 'Conteúdo em breve.')}>
                                <Text style={[
                                    styles.legalText,
                                    ultraCompactLogin && styles.legalTextUltraCompact,
                                ]}>Termos de Uso</Text>
                            </TouchableOpacity>
                            <Text style={[
                                styles.legalSeparator,
                                ultraCompactLogin && styles.legalTextUltraCompact,
                            ]}>•</Text>
                            <TouchableOpacity onPress={() => Alert.alert('Privacidade', 'Conteúdo em breve.')}>
                                <Text style={[
                                    styles.legalText,
                                    ultraCompactLogin && styles.legalTextUltraCompact,
                                ]}>Politica de Privacidade</Text>
                            </TouchableOpacity>
                        </View>
                    </ScrollView>
                </Animated.View>
                </View>
            </KeyboardAvoidingView>

            <Modal
                visible={showAccountCreatedModal}
                transparent
                animationType="fade"
                statusBarTranslucent
                onRequestClose={handleAccountCreatedContinue}
            >
                <View style={styles.successOverlay}>
                    <View style={styles.successCard}>
                        <View style={styles.successIconOuter}>
                            <View style={styles.successIconInner}>
                                <Text style={styles.successIconText}>OK</Text>
                            </View>
                        </View>

                        <Text style={styles.successTitle}>Conta criada</Text>
                        <Text style={styles.successMessage}>
                            Verifique seu email para confirmar o cadastro e liberar o acesso completo.
                        </Text>

                        {!!createdAccountEmail && (
                            <View style={styles.successEmailPill}>
                                <Text style={styles.successEmailText} numberOfLines={1}>
                                    {createdAccountEmail}
                                </Text>
                            </View>
                        )}

                        <TouchableOpacity
                            style={styles.successButton}
                            onPress={handleAccountCreatedContinue}
                            activeOpacity={0.85}
                        >
                            <Text style={styles.successButtonText}>Entendi</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </Modal>

            <Modal
                visible={forgotVisible}
                transparent
                animationType="fade"
                statusBarTranslucent
                onRequestClose={() => setForgotVisible(false)}
            >
                <KeyboardAvoidingView
                    style={styles.successOverlay}
                    behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                >
                    <View style={styles.successCard}>
                        <Text style={styles.successTitle}>Recuperar senha</Text>
                        <Text style={styles.successMessage}>
                            Digite seu e-mail e enviaremos um link para você redefinir sua senha.
                        </Text>

                        <View style={[styles.fieldControl, { marginTop: 18 }]}>
                            <TextInput
                                style={styles.fieldInput}
                                placeholder="voce@email.com"
                                placeholderTextColor="#A0A3B1"
                                value={forgotEmail}
                                onChangeText={setForgotEmail}
                                keyboardType="email-address"
                                autoCapitalize="none"
                                autoCorrect={false}
                                editable={!forgotLoading}
                                autoFocus
                            />
                        </View>

                        <TouchableOpacity
                            style={[styles.successButton, forgotLoading && { opacity: 0.7 }]}
                            onPress={handleSendReset}
                            disabled={forgotLoading}
                            activeOpacity={0.85}
                        >
                            {forgotLoading ? (
                                <ActivityIndicator color="#FFFFFF" />
                            ) : (
                                <Text style={styles.successButtonText}>Enviar link</Text>
                            )}
                        </TouchableOpacity>

                        <TouchableOpacity
                            onPress={() => setForgotVisible(false)}
                            disabled={forgotLoading}
                            activeOpacity={0.75}
                            style={styles.forgotModalCancel}
                        >
                            <Text style={styles.forgotPasswordText}>Cancelar</Text>
                        </TouchableOpacity>
                    </View>
                </KeyboardAvoidingView>
            </Modal>
        </View>
    );
};

export default UserAuthScreen;
